import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import type { Stripe } from 'stripe';
import { AppConfigService } from '../providers/config.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SentryService } from './sentry.service';
@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private config = inject(AppConfigService);
  private observableLoader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private sentryService = inject(SentryService);

  public get invoices$() {
    return this.observableLoader.get(
      'invoices',
      () =>
        this.getAllService.getAllStripe<Stripe.Invoice>(`${this.config.apiEndpoint}/billing/invoices`).pipe(
          map(resp =>
            resp.map(
              invoice =>
                ({
                  ...invoice,
                  description: this.getInvoiceDescription(invoice),
                }) as Stripe.Invoice
            )
          ),
          catchError(err => {
            console.error(err);
            // Returning an empty array here will prevent the loader from trying to load the data again
            this.sentryService.captureException(err);
            return of([]) as Observable<Stripe.Invoice[]>;
          })
        ),
      1000 * 60 * 2
    );
  }

  public getLatestUnpaidInvoice() {
    return this.invoices$.pipe(map(invoices => invoices.find(invoice => invoice.status === 'open')));
  }

  public clearInvoices() {
    this.observableLoader.remove('invoices');
  }

  private getInvoiceDescription(invoice: Stripe.Invoice) {
    if (['subscription_create', 'subscription_cycle'].includes(invoice.billing_reason)) {
      if (invoice.lines.data[0].price.recurring.interval === 'year') return 'Yearly Renewal';
      if (invoice.lines.data[0].price.recurring.interval === 'month') return 'Monthly Renewal';
    }
    return 'Prorated Invoice';
  }
}
